/* eslint-disable */
// Shared constants and helpers for tenant management
import { isIrishProperty } from '../../utils/propertyCountry';
import { symbolFor } from '../../utils/currencyTotals';

export const fmtDate = (d) => {
  if (!d) return '—';
  const s = typeof d === 'string' ? d.substring(0, 10) : new Date(d).toISOString().substring(0, 10);
  const [y, m, day] = s.split('-');
  return `${day}/${m}/${y}`;
};

// BUG-005e: one rule for the symbol (shared helper)
export const currencySymbol = (currency) => symbolFor(currency);

export const fmtRent = (amount, currency) => {
  if (!amount) return '—';
  const sym = currencySymbol(currency);
  return `${sym}${parseFloat(amount).toLocaleString('en-GB', { minimumFractionDigits: 2 })}`;
};

export const daysBetween = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr.substring(0, 10) + 'T12:00:00');
  return Math.round((d - new Date()) / 86400000);
};

export const leaseStatus = (t) => {
  if (!t.lease_end) return { label: 'Periodic / Rolling', color: 'var(--text-secondary)' };
  const days = daysBetween(t.lease_end);
  if (days < 0)   return { label: 'Expired',              color: 'var(--danger)' };
  if (days <= 30) return { label: `Expires in ${days}d`,  color: 'var(--danger)' };
  if (days <= 60) return { label: `Expires in ${days}d`,  color: 'var(--warning)' };
  return { label: fmtDate(t.lease_end), color: 'var(--success)' };
};

export const TENANCY_TYPES = {
  england: [
    { value: 'assured_periodic',    label: 'Assured Periodic Tenancy (post-RRA)' },
    { value: 'assured_fixed',       label: 'Assured Tenancy Fixed Term (pre-RRA)' },
    { value: 'licence',             label: 'Licence to Occupy' },
    { value: 'company_let',         label: 'Company Let' },
    { value: 'student_hmo',         label: 'Student HMO Licence' },
  ],
  scotland: [
    { value: 'prt',                 label: 'Private Residential Tenancy (PRT)' },
    { value: 'licence',             label: 'Licence to Occupy' },
  ],
  wales: [
    { value: 'occupation_contract', label: 'Occupation Contract' },
    { value: 'licence',             label: 'Licence to Occupy' },
  ],
  northern_ireland: [
    { value: 'private_tenancy',     label: 'Private Tenancy' },
    { value: 'licence',             label: 'Licence to Occupy' },
  ],
  ireland: [
    { value: 'part4',               label: 'Part 4 Tenancy' },
    { value: 'further_part4',       label: 'Further Part 4 Tenancy' },
    { value: 'fixed_term',          label: 'Fixed Term Tenancy' },
    { value: 'licence',             label: 'Licence to Occupy' },
    { value: 'student_specific',    label: 'Student Specific Accommodation' },
  ],
};

export const DEPOSIT_SCHEMES = {
  england:          ['DPS (Deposit Protection Service)', 'MyDeposits', 'TDS (Tenancy Deposit Scheme)', 'Other'],
  scotland:         ['SafeDeposits Scotland', 'MyDeposits Scotland', 'Letting Protection Service Scotland', 'Other'],
  wales:            ['DPS (Deposit Protection Service)', 'MyDeposits', 'TDS (Tenancy Deposit Scheme)', 'Other'],
  northern_ireland: ['TDS Northern Ireland', 'Other'],
  ireland:          ['RTB Deposit Protection (incoming)', 'Held by landlord', 'Held by agency', 'Other'],
};

export const CHECKLIST_ITEMS = {
  england: [
    { key: 'tenancy_agreement_signed',   label: 'Tenancy agreement signed' },
    { key: 'deposit_taken',              label: 'Deposit taken' },
    { key: 'deposit_protected',          label: 'Deposit protected in scheme' },
    { key: 'right_to_rent_checked',      label: 'Right to Rent check completed' },
    { key: 'inventory_completed',        label: 'Inventory / check-in report completed' },
    { key: 'keys_handed_over',           label: 'Keys handed over' },
    { key: 'standing_order_confirmed',   label: 'Standing order / direct debit set up' },
    { key: 'gas_safety_cert_provided',   label: 'Gas Safety Certificate provided' },
    { key: 'epc_provided',               label: 'EPC provided' },
    { key: 'how_to_rent_guide_provided', label: '"How to Rent" guide provided' },
  ],
  scotland: [
    { key: 'tenancy_agreement_signed',   label: 'Tenancy agreement signed' },
    { key: 'deposit_taken',              label: 'Deposit taken' },
    { key: 'deposit_protected',          label: 'Deposit protected in scheme' },
    { key: 'inventory_completed',        label: 'Inventory completed' },
    { key: 'keys_handed_over',           label: 'Keys handed over' },
    { key: 'gas_safety_cert_provided',   label: 'Gas Safety Certificate provided' },
    { key: 'epc_provided',               label: 'EPC provided' },
    { key: 'tenants_rights_info',        label: 'Tenant rights information provided' },
  ],
  wales: [
    { key: 'tenancy_agreement_signed',   label: 'Occupation contract signed' },
    { key: 'deposit_taken',              label: 'Deposit taken' },
    { key: 'deposit_protected',          label: 'Deposit protected in scheme' },
    { key: 'right_to_rent_checked',      label: 'Right to Rent check completed' },
    { key: 'inventory_completed',        label: 'Inventory completed' },
    { key: 'keys_handed_over',           label: 'Keys handed over' },
    { key: 'gas_safety_cert_provided',   label: 'Gas Safety Certificate provided' },
    { key: 'epc_provided',               label: 'EPC provided' },
  ],
  northern_ireland: [
    { key: 'tenancy_agreement_signed',   label: 'Tenancy agreement signed' },
    { key: 'deposit_taken',              label: 'Deposit taken' },
    { key: 'deposit_protected',          label: 'Deposit protected in scheme' },
    { key: 'inventory_completed',        label: 'Inventory completed' },
    { key: 'keys_handed_over',           label: 'Keys handed over' },
    { key: 'gas_safety_cert_provided',   label: 'Gas Safety Certificate provided' },
    { key: 'epc_provided',               label: 'EPC provided' },
  ],
  ireland: [
    { key: 'tenancy_agreement_signed',   label: 'Tenancy agreement signed' },
    { key: 'deposit_taken',              label: 'Deposit taken' },
    { key: 'deposit_protected',          label: 'Deposit held (per RTB rules)' },
    { key: 'rtb_registered',             label: 'Tenancy registered with RTB' },
    { key: 'inventory_completed',        label: 'Inventory completed' },
    { key: 'keys_handed_over',           label: 'Keys handed over' },
    { key: 'gas_safety_cert_provided',   label: 'Gas Safety Certificate provided' },
    { key: 'ber_cert_provided',          label: 'BER Certificate provided' },
    { key: 'rent_book_provided',         label: 'Rent book provided' },
    { key: 'rpz_compliance_confirmed',   label: 'RPZ compliance confirmed (if applicable)' },
  ],
};

export const getIrishComplianceIssues = (t) => {
  if (!isIrishProperty(t)) return [];
  const issues = [];
  const now = new Date();

  if (!t.rtb_registered && t.lease_start) {
    const leaseStart = new Date(t.lease_start);
    const daysSince = Math.floor((now - leaseStart) / 86400000);
    if (daysSince >= 25) {
      const deadline = new Date(leaseStart);
      deadline.setDate(deadline.getDate() + 30);
      issues.push({
        code: 'IC-001',
        label: 'RTB Registration Due',
        detail: `Deadline: ${deadline.toLocaleDateString('en-IE')}`,
        severity: daysSince >= 30 ? 'danger' : 'warning',
      });
    }
  }

  if (t.rpz_property) {
    const reviewBase = t.last_rent_review_date || t.lease_start;
    if (reviewBase) {
      const reviewDue = new Date(reviewBase);
      reviewDue.setFullYear(reviewDue.getFullYear() + 1);
      reviewDue.setDate(reviewDue.getDate() - 30);
      if (now >= reviewDue) issues.push({ code: 'IC-002', label: 'Rent Review Due', detail: 'Review allowable every 12 months', severity: 'warning' });
    }
  }

  if (t.lease_start) {
    const sixYears = new Date(t.lease_start);
    sixYears.setFullYear(sixYears.getFullYear() + 6);
    const daysTo6yr = Math.floor((sixYears - now) / 86400000);
    if (daysTo6yr >= 0 && daysTo6yr <= 60) {
      issues.push({ code: 'IC-005', label: `Further Part 4 in ${daysTo6yr}d`, detail: `6 year anniversary: ${sixYears.toLocaleDateString('en-IE')}`, severity: 'warning' });
    }
  }

  return issues;
};

export const PROPERTY_TYPE_LABELS = {
  hmo:         'HMO',
  btl:         'Buy to Let',
  single_let:  'Single Let',
  holiday_let: 'Holiday Let',
  commercial:  'Commercial',
};
